<template>
  <BaseModal modal-class="archive-diet-modal" @close="close">
    <template #title>
      <div class="archive-diet-modal__header">
        <div class="archive-diet-modal__icon-wrapper"><BaseIcon name="archive" size="24" /></div>
      </div>
    </template>
    <div class="archive-diet-modal__content">
      <p class="archive-diet-modal__title">Archiwizować jadłospis?</p>
      <p class="archive-diet-modal__description">
        Jadłospis zostanie oznaczony statusem “Archiwalny”, co uniemożliwi jego edycję. Nadal będzie
        można go skopiować jako szkic i modyfikować kopię.
      </p>
    </div>
    <template #actions>
      <StyledButton variant="outline" @click="close">Anuluj</StyledButton>
      <StyledButton
        :disabled="archiveDietMutation.isPending.value"
        type="error"
        variant="primary"
        @click="archiveDietMutation.mutate()"
      >
        Archiwizuj jadłospis
      </StyledButton>
      <BaseLoader v-if="archiveDietMutation.isPending.value" class="base-loader--size-12" />
    </template>
  </BaseModal>
</template>

<script lang="ts" setup>
  import { useMutation, useQueryClient } from '@tanstack/vue-query'

  import BaseIcon from '@/components/BaseIcon.vue'
  import BaseLoader from '@/components/BaseLoader.vue'
  import BaseModal from '@/components/BaseModal.vue'
  import { StyledButton } from '@/components/StyledButton'
  import { dietsService } from '@/services/dietsService'
  import { useGlobalStore } from '@/store/globalStore'
  import { reportError } from '@/utils/reportError'

  const props = defineProps<{
    dietId: number
  }>()

  const emit = defineEmits<{
    close: []
    success: []
  }>()

  const globalStore = useGlobalStore()
  const queryClient = useQueryClient()

  const archiveDietMutation = useMutation({
    mutationFn: async () => {
      await dietsService.updateDietStatus(props.dietId, 3) // 3 = ARCHIVE
    },
    onError: (error: unknown) => {
      globalStore.createMessage({
        title: 'Nie udało się zarchiwizować jadłospisu',
        type: 'error',
      })
      reportError(error, 'Error while archiving diet', {
        dietId: props.dietId,
      })
    },
    onSuccess: () => {
      globalStore.createMessage({
        title: 'Jadłospis został zarchiwizowany',
      })
      void queryClient.invalidateQueries({ queryKey: ['patientMealPlans'] })
      emit('success')
      close()
    },
  })

  const close = () => {
    emit('close')
  }
</script>

<style scoped>
  .archive-diet-modal__header {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
  }

  .archive-diet-modal__icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    background-color: #fee2e2;
    border-radius: 50%;
  }

  .archive-diet-modal__icon-wrapper :deep(.base-icon) {
    color: #ef4444;
  }

  .archive-diet-modal__title {
    margin-bottom: 12px;
    font-size: 18px;
    font-weight: 600;
    line-height: 28px;
    color: var(--color-primary-text);
  }

  .archive-diet-modal__description {
    font-size: 14px;
    line-height: 20px;
    color: var(--color-primary-text);
    text-align: left;
  }
</style>
